import {
	GenericSetting,
	PortalCustomColor,
	PortalCustomCssClass,
	PortalCustomGradient,
	PortalSettingMap,
} from '../Types/portal';
import { get, getQSFromObject, post } from './common';
import { getGuid } from '../Utils/stringUtils';
import { SerializableMiniSettings, mergeMiniSettings } from '../Types/admin/miniSettings';

const apiGenericSettingPath = 'api/genericsetting/';

const systemGoogleFontStylesheetUrls = [
	'https://fonts.googleapis.com/css2?family=Montserrat:wght@100..900&family=Source+Sans+Pro:wght@300;400;600;700;900&family=Roboto:wght@400;900&family=Open+Sans:wght@400;700&display=swap',
];

const defaultTinyMceFontFamilyFormats =
	'Andale Mono=andale mono,monospace;' +
	'Arial=arial,helvetica,sans-serif;' +
	'Arial Black=arial black,sans-serif;' +
	'Book Antiqua=book antiqua,palatino,serif;' +
	'Comic Sans MS=comic sans ms,sans-serif;' +
	'Courier New=courier new,courier,monospace;' +
	'Georgia=georgia,palatino,serif;' +
	'Helvetica=helvetica,arial,sans-serif;' +
	'Impact=impact,sans-serif;' +
	'Symbol=symbol;' +
	'Tahoma=tahoma,arial,helvetica,sans-serif;' +
	'Terminal=terminal,monaco,monospace;' +
	'Times New Roman=times new roman,times,serif;' +
	'Trebuchet MS=trebuchet ms,geneva,sans-serif;' +
	'Verdana=verdana,geneva,sans-serif;' +
	'Webdings=webdings;' +
	'Wingdings=wingdings,zapf dingbats';

export type PortalFontFamilyOption = {
	label: string;
	value: string;
	group: 'custom' | 'system' | 'default';
};

const systemGoogleFontFamilyOptions: PortalFontFamilyOption[] = [
	{ label: 'Montserrat', value: "'Montserrat', sans-serif", group: 'system' },
	{ label: 'Source Sans Pro', value: "'Source Sans Pro', sans-serif", group: 'system' },
	{ label: 'Roboto', value: "'Roboto', sans-serif", group: 'system' },
	{ label: 'Open Sans', value: "'Open Sans', sans-serif", group: 'system' },
];

const defaultWebFontFamilyOptions: PortalFontFamilyOption[] = [
	{ label: 'Arial', value: 'Arial, Helvetica, sans-serif', group: 'default' },
	{ label: 'Georgia', value: 'Georgia, serif', group: 'default' },
	{ label: 'Helvetica', value: 'Helvetica, Arial, sans-serif', group: 'default' },
	{ label: 'Tahoma', value: 'Tahoma, Arial, sans-serif', group: 'default' },
	{ label: 'Times New Roman', value: "'Times New Roman', Times, serif", group: 'default' },
	{ label: 'Trebuchet MS', value: "'Trebuchet MS', Geneva, sans-serif", group: 'default' },
	{ label: 'Verdana', value: 'Verdana, Geneva, sans-serif', group: 'default' },
	{ label: 'Monospace', value: 'monospace', group: 'default' },
	{ label: 'Sans Serif', value: 'sans-serif', group: 'default' },
	{ label: 'Serif', value: 'serif', group: 'default' },
];

export function getPortalSetting<K extends keyof PortalSettingMap>(key: K): PortalSettingMap[K] {
	const value = window.portalSettings?.[key];
	return value as PortalSettingMap[K];
}

function parsePortalSettingList<T>(rawValue: string | undefined, normalize: (item: unknown) => T | null): T[] {
	if (!rawValue) return [];

	try {
		const parsed = JSON.parse(rawValue);
		if (!Array.isArray(parsed)) return [];
		return parsed.map(normalize).filter((item): item is T => item !== null);
	} catch {
		return [];
	}
}

export function getPortalCustomColors(): PortalCustomColor[] {
	return parsePortalSettingList(getPortalSetting('CustomerColors'), (item) => {
		if (!item || typeof item !== 'object') return null;
		const candidate = item as Partial<PortalCustomColor>;
		if (!candidate.id || !candidate.name || !candidate.color) return null;

		return {
			id: candidate.id,
			name: candidate.name,
			color: candidate.color,
			cssVariable: candidate.cssVariable || `--biz-custom-color-${createPortalSettingSlug(candidate.name || candidate.id)}`,
		};
	});
}

function createPortalSettingSlug(value: string): string {
	return value
		.trim()
		.toLowerCase()
		.replace(/[^a-z0-9]+/g, '-')
		.replace(/^-+|-+$/g, '');
}

export function getPortalCustomColorCssVariable(color: PortalCustomColor): string {
	return `var(${color.cssVariable || `--biz-custom-color-${createPortalSettingSlug(color.name || color.id)}`})`;
}

export function getPortalCustomColorCssValue(color: PortalCustomColor): string {
	return color.color;
}

export function getPortalCustomGradients(): PortalCustomGradient[] {
	return parsePortalSettingList(getPortalSetting('CustomerGradients'), (item) => {
		if (!item || typeof item !== 'object') return null;
		const candidate = item as Partial<PortalCustomGradient>;
		if (!candidate.id || !candidate.name || !candidate.type || !Array.isArray(candidate.colors)) return null;
		if (candidate.type !== 'linear' && candidate.type !== 'radial') return null;

		return {
			id: candidate.id,
			name: candidate.name,
			cssVariable: candidate.cssVariable || `--biz-custom-gradient-${createPortalSettingSlug(candidate.name || candidate.id)}`,
			type: candidate.type,
			direction: typeof candidate.direction === 'number' ? candidate.direction : 90,
			colors: candidate.colors.filter((color): color is string => typeof color === 'string' && color.length > 0),
		};
	});
}

export function getPortalCustomCssClasses(): PortalCustomCssClass[] {
	return parsePortalSettingList(getPortalSetting('CustomerCssClasses'), (item) => {
		if (!item || typeof item !== 'object') return null;
		const candidate = item as Partial<PortalCustomCssClass>;
		if (!candidate.id || !candidate.name || !candidate.className) return null;

		return {
			id: candidate.id,
			name: candidate.name,
			className: candidate.className,
			compatibleModules: Array.isArray(candidate.compatibleModules)
				? candidate.compatibleModules.filter((moduleType): moduleType is string => typeof moduleType === 'string' && moduleType.length > 0)
				: [],
		};
	});
}

function extractGoogleFontStylesheetUrl(rawValue: string): string | null {
	const trimmedValue = rawValue.trim();
	if (!trimmedValue) return null;

	const importMatch = trimmedValue.match(/@import\s+url\((['"]?)([^'")]+)\1\)\s*;?/i);
	const linkMatch = trimmedValue.match(/href\s*=\s*['"]([^'"]+)['"]/i);
	const urlCandidate = importMatch?.[2] || linkMatch?.[1] || trimmedValue;

	const normalizedUrl = normalizeGoogleFontStylesheetUrl(urlCandidate);
	return normalizedUrl;
}

function normalizeGoogleFontStylesheetUrl(rawUrl: string): string | null {
	const trimmedUrl = rawUrl.trim();
	if (!trimmedUrl) return null;

	const absoluteUrl = trimmedUrl.startsWith('//')
		? `https:${trimmedUrl}`
		: trimmedUrl.replace(/^http:\/\//i, 'https://');

	try {
		const url = new URL(absoluteUrl);
		if (url.protocol !== 'https:') return null;
		if (url.hostname !== 'fonts.googleapis.com') return null;
		if (!url.pathname.startsWith('/css')) return null;
		return url.toString();
	} catch {
		return null;
	}
}

function getFontFamiliesFromGoogleFontUrl(urlString: string): string[] {
	try {
		const url = new URL(urlString);
		return url.searchParams
			.getAll('family')
			.map((family) => {
				const decodedFamily = decodeURIComponent(family).split(':')[0].replace(/\+/g, ' ').trim();
				return decodedFamily;
			})
			.filter(Boolean);
	} catch {
		return [];
	}
}

export function getPortalGoogleFontStylesheetUrls(): string[] {
	const rawValue = getPortalSetting('GoogleFontStylesheets');
	if (!rawValue) return [];

	const urls = rawValue
		.split(/\r?\n/)
		.map(extractGoogleFontStylesheetUrl)
		.filter((url): url is string => Boolean(url));

	return Array.from(new Set(urls));
}

function getAllGoogleFontStylesheetUrls(): string[] {
	return Array.from(new Set([...systemGoogleFontStylesheetUrls, ...getPortalGoogleFontStylesheetUrls()]));
}

export function getPortalGoogleFontCssImports(): string {
	return getAllGoogleFontStylesheetUrls()
		.map((url) => `@import url('${url}');`)
		.join('\n');
}

export function getPortalGoogleFontFamilyFormats(): string | undefined {
	const systemFamilies = systemGoogleFontStylesheetUrls
		.flatMap(getFontFamiliesFromGoogleFontUrl)
		.filter((family, index, allFamilies) => allFamilies.indexOf(family) === index);

	const customFamilies = getPortalGoogleFontStylesheetUrls()
		.flatMap(getFontFamiliesFromGoogleFontUrl)
		.filter((family, index, allFamilies) => allFamilies.indexOf(family) === index);

	const families = [...customFamilies, ...systemFamilies].filter(
		(family, index, allFamilies) => allFamilies.indexOf(family) === index
	);

	if (families.length === 0) return defaultTinyMceFontFamilyFormats;

	const customFormats = families
		.map((family) => {
			const cssFamilyName = family.includes(' ') ? `'${family}'` : family;
			return `${family}=${cssFamilyName},sans-serif`;
		})
		.join(';');

	return `${customFormats};${defaultTinyMceFontFamilyFormats}`;
}

export function getPortalFontFamilyOptions(): PortalFontFamilyOption[] {
	const customOptions = getPortalGoogleFontStylesheetUrls()
		.flatMap(getFontFamiliesFromGoogleFontUrl)
		.filter((family, index, allFamilies) => allFamilies.indexOf(family) === index)
		.map((family) => ({
			label: family,
			value: family.includes(' ') ? `'${family}', sans-serif` : `${family}, sans-serif`,
			group: 'custom' as const,
		}));

	const dedupedOptions = [...customOptions, ...systemGoogleFontFamilyOptions, ...defaultWebFontFamilyOptions].filter(
		(option, index, allOptions) => allOptions.findIndex((candidate) => candidate.value === option.value) === index
	);

	return dedupedOptions;
}

export async function savePortalSetting<K extends keyof PortalSettingMap>(key: K, value: PortalSettingMap[K]) {
	await post('api/portal/SavePortalSetting', {
		Key: key,
		Value: value,
	});

	window.portalSettings = window.portalSettings || {};
	window.portalSettings[key] = value;
	return value;
}

export async function savePortalCustomColor(params: { name: string; color: string }): Promise<PortalCustomColor[]> {
	const nextColors = [
		...getPortalCustomColors(),
		{
			id: getGuid(),
			name: params.name.trim(),
			color: params.color,
			cssVariable: `--biz-custom-color-${createPortalSettingSlug(params.name.trim())}`,
		},
	];

	await savePortalSetting('CustomerColors', JSON.stringify(nextColors));
	return nextColors;
}

let _mergedMiniSettings: SerializableMiniSettings | null = null;

function getMergedMiniSettings(): SerializableMiniSettings {
	if (!_mergedMiniSettings) {
		_mergedMiniSettings = mergeMiniSettings(window.savedMiniSettings ?? null);
	}
	return _mergedMiniSettings;
}

export function getMiniSetting<
	G extends keyof SerializableMiniSettings,
	K extends keyof SerializableMiniSettings[G]
>(group: G, key: K): SerializableMiniSettings[G][K] {
	const settings = getMergedMiniSettings();
	return settings[group][key];
}

export async function getGenericSettings(type: string, keys: string[]) {
	const path = `${apiGenericSettingPath}GetGenericSettings?${getQSFromObject({ type: type, keys })}`;
	return await get<GenericSetting[]>(path);
}
